// frontend/src/services/duplicateOvertimeCheck.service.ts
import {
    collection,
    query,
    where,
    getDocs,
    Timestamp
  } from 'firebase/firestore';
  import { db } from './firebase';
  
  export interface OvertimeConflict {
    employeeId: string;
    employeeName: string;
    employeePin: string;
    existingRequestId: string;
    existingProjectName: string;
    existingProjectCode: string;
    existingStartTime: Date;
    existingEndTime: Date;
    existingStatus: string;
    conflictType: 'same_project' | 'time_overlap' | 'different_project';
    canProceed: boolean;
    reason: string;
  }
  
  export interface DuplicateCheckResult {
    hasConflicts: boolean;
    conflicts: OvertimeConflict[];
    canProceedWithAll: boolean;
    message: string;
    suggestions: string[];
  }
  
  class DuplicateOvertimeCheckService {
    /**
     * Check for duplicate overtime assignments for selected employees
     */
    async checkDuplicateOvertime(
      employeeIds: string[],
      overtimeDate: Date,
      projectCode: string,
      projectName: string,
      startTime: Date,
      endTime: Date,
      currentRequestId?: string // For edit mode
    ): Promise<DuplicateCheckResult> {
      try {
        console.log('🔍 Checking for duplicate overtime assignments...');
        console.log('📅 Date:', overtimeDate.toISOString().split('T')[0]);
        console.log('🏢 Project:', projectCode, '-', projectName);
        console.log('⏰ Time:', startTime.toTimeString().slice(0, 5), '-', endTime.toTimeString().slice(0, 5));
        console.log('👥 Employees:', employeeIds.length);
  
        if (employeeIds.length === 0) {
          return {
            hasConflicts: false,
            conflicts: [],
            canProceedWithAll: true,
            message: 'No employees selected',
            suggestions: []
          };
        }
  
        // Get the date string for querying
        const dateString = overtimeDate.toISOString().split('T')[0];
        const overtimeDateStart = new Date(dateString);
        overtimeDateStart.setHours(0, 0, 0, 0);
        const overtimeDateEnd = new Date(dateString);
        overtimeDateEnd.setHours(23, 59, 59, 999);
  
        // Query all overtime requests for this date
        const overtimeRequestsQuery = query(
          collection(db, 'overtime_requests'),
          where('overtimeDate', '>=', Timestamp.fromDate(overtimeDateStart)),
          where('overtimeDate', '<=', Timestamp.fromDate(overtimeDateEnd))
        );
  
        const snapshot = await getDocs(overtimeRequestsQuery);
        console.log(`📊 Found ${snapshot.size} overtime requests for ${dateString}`);
  
        const conflicts: OvertimeConflict[] = [];
        const employeeRequestMap = new Map<string, any[]>();
  
        // Build a map of employee ID to their existing requests
        for (const doc of snapshot.docs) {
          const data = doc.data();
          const requestId = doc.id;
  
          // Skip if checking against current request (edit mode)
          if (currentRequestId && requestId === currentRequestId) {
            continue;
          }
  
          // Skip rejected requests
          if (data.status === 'rejected') {
            continue;
          }
  
          const requestEmployeeIds = data.employeeIds || [];
          const requestStartTime = data.startTime?.toDate();
          const requestEndTime = data.endTime?.toDate();
  
          for (const empId of requestEmployeeIds) {
            if (employeeIds.includes(empId)) {
              if (!employeeRequestMap.has(empId)) {
                employeeRequestMap.set(empId, []);
              }
              employeeRequestMap.get(empId)!.push({
                requestId,
                projectName: data.projectName,
                projectCode: data.projectCode,
                startTime: requestStartTime,
                endTime: requestEndTime,
                status: data.status,
                employeeDetails: data.employeeDetails || []
              });
            }
          }
        }
  
        console.log(`🔍 Found existing requests for ${employeeRequestMap.size} employees`);
  
        // Analyze conflicts for each employee
        for (const [empId, requests] of employeeRequestMap) {
          for (const request of requests) {
            const isSameProject = request.projectCode === projectCode;
            const hasTimeOverlap = this.checkTimeOverlap(
              startTime,
              endTime,
              request.startTime,
              request.endTime
            );
  
            // Get employee details
            const empDetails = request.employeeDetails.find((e: any) => e.id === empId) || {
              name: 'Unknown Employee',
              employeeNumber: empId
            };
  
            let conflictType: 'same_project' | 'time_overlap' | 'different_project';
            let canProceed: boolean;
            let reason: string;
  
            if (isSameProject) {
              // Same project - absolute conflict
              conflictType = 'same_project';
              canProceed = false;
              reason = `Already assigned to the same project (${request.projectCode}). Cannot create duplicate assignment.`;
            } else if (hasTimeOverlap) {
              // Different project but time overlaps
              conflictType = 'time_overlap';
              canProceed = false;
              reason = `Time conflict with another project (${request.projectCode}). Employee cannot work on two projects simultaneously.`;
            } else {
              // Different project, different time - allowed
              conflictType = 'different_project';
              canProceed = true;
              reason = `Already assigned to project ${request.projectCode} at different time. This assignment is allowed.`;
            }
  
            conflicts.push({
              employeeId: empId,
              employeeName: empDetails.name,
              employeePin: empDetails.employeeNumber || empId.replace('EMP', ''),
              existingRequestId: request.requestId,
              existingProjectName: request.projectName,
              existingProjectCode: request.projectCode,
              existingStartTime: request.startTime,
              existingEndTime: request.endTime,
              existingStatus: request.status,
              conflictType,
              canProceed,
              reason
            });
          }
        }
  
        // Determine if we can proceed with all employees
        const blockingConflicts = conflicts.filter(c => !c.canProceed);
        const canProceedWithAll = blockingConflicts.length === 0;
  
        // Generate suggestions
        const suggestions = this.generateSuggestions(conflicts, projectCode, startTime, endTime);
  
        // Generate message
        let message = '';
        if (conflicts.length === 0) {
          message = '✅ No conflicts found. All employees are available for this overtime.';
        } else if (canProceedWithAll) {
          message = `ℹ️ ${conflicts.length} employee(s) have other overtime assignments at different times. This is allowed.`;
        } else {
          message = `⚠️ ${blockingConflicts.length} employee(s) cannot be assigned due to conflicts.`;
        }
  
        console.log('✅ Duplicate check completed:', {
          totalConflicts: conflicts.length,
          blockingConflicts: blockingConflicts.length,
          canProceed: canProceedWithAll
        });
  
        return {
          hasConflicts: conflicts.length > 0,
          conflicts,
          canProceedWithAll,
          message,
          suggestions
        };
  
      } catch (error) {
        console.error('❌ Error checking duplicate overtime:', error);
        throw new Error(`Duplicate check failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  
    /**
     * Check if two time ranges overlap
     */
    private checkTimeOverlap(
      start1: Date,
      end1: Date,
      start2: Date,
      end2: Date
    ): boolean {
      return start1 < end2 && end1 > start2;
    }
  
    /**
     * Generate suggestions for resolving conflicts
     */
    private generateSuggestions(
      conflicts: OvertimeConflict[],
      currentProjectCode: string,
      currentStartTime: Date,
      currentEndTime: Date
    ): string[] {
      const suggestions: string[] = [];
  
      const sameProjectConflicts = conflicts.filter(c => c.conflictType === 'same_project');
      const timeOverlapConflicts = conflicts.filter(c => c.conflictType === 'time_overlap');
  
      if (sameProjectConflicts.length > 0) {
        suggestions.push(
          `✏️ Change the project code - ${sameProjectConflicts.length} employee(s) already assigned to project "${currentProjectCode}"`
        );
        suggestions.push(
          `❌ Remove conflicting employees from selection and create separate request`
        );
        suggestions.push(
          `🔧 Edit the existing request to add more employees instead of creating new one`
        );
      }
  
      if (timeOverlapConflicts.length > 0) {
        suggestions.push(
          `⏰ Change the overtime schedule - ${timeOverlapConflicts.length} employee(s) have time conflicts`
        );
        suggestions.push(
          `📅 Schedule overtime for a different time that doesn't overlap with existing assignments`
        );
      }
  
      if (suggestions.length === 0) {
        suggestions.push('✅ No changes needed - you can proceed with this request');
      }
  
      return suggestions;
    }
  
    /**
     * Get detailed conflict information for display
     */
    getConflictSummary(conflicts: OvertimeConflict[]): {
      sameProject: OvertimeConflict[];
      timeOverlap: OvertimeConflict[];
      allowedDifferent: OvertimeConflict[];
    } {
      return {
        sameProject: conflicts.filter(c => c.conflictType === 'same_project'),
        timeOverlap: conflicts.filter(c => c.conflictType === 'time_overlap'),
        allowedDifferent: conflicts.filter(c => c.conflictType === 'different_project')
      };
    }
  
    /**
     * Format conflict for display
     */
    formatConflictMessage(conflict: OvertimeConflict): string {
      const time = `${conflict.existingStartTime.toTimeString().slice(0, 5)} - ${conflict.existingEndTime.toTimeString().slice(0, 5)}`;
      
      switch (conflict.conflictType) {
        case 'same_project':
          return `${conflict.employeeName} (${conflict.employeePin}) is already assigned to ${conflict.existingProjectCode} at ${time} - Status: ${conflict.existingStatus}`;
        
        case 'time_overlap':
          return `${conflict.employeeName} (${conflict.employeePin}) has time conflict with ${conflict.existingProjectCode} (${time})`;
        
        case 'different_project':
          return `${conflict.employeeName} (${conflict.employeePin}) has other assignment at different time (${conflict.existingProjectCode} - ${time})`;
        
        default:
          return `${conflict.employeeName} has a conflict`;
      }
    }
  }
  
  export const duplicateOvertimeCheckService = new DuplicateOvertimeCheckService();